import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { useAdobeFonts } from '../hooks/useAdobeFonts.js';

const CATEGORIES = ['All', 'sans-serif', 'serif', 'display', 'handwriting', 'monospace'];

export default function AdobeFontsBrowser({ onClose, onImported }) {
  const { theme } = useTheme();
  const { fonts, total, loading, error, search, importFont } = useAdobeFonts();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(null);
  const [imported, setImported] = useState(new Set());

  useEffect(() => {
    const params = { page, limit: 30 };
    if (query) params.search = query;
    if (category !== 'All') params.category = category;
    search(params);
  }, [query, category, page, search]);

  const handleImport = async (font) => {
    setImporting(font.family);
    try {
      await importFont({
        family: font.family,
        category: font.category,
        styleCount: font.styleCount,
      });
      setImported(prev => new Set([...prev, font.family]));
      onImported?.();
    } catch (err) {
      if (err.status === 409) setImported(prev => new Set([...prev, font.family]));
    } finally {
      setImporting(null);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 800, maxHeight: '85vh',
          background: theme.bgElevated, borderRadius: 16,
          border: `1px solid ${theme.borderMedium}`,
          boxShadow: theme.shadow, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.text }}>Adobe Fonts</h3>
            <span style={{ fontSize: 11, color: theme.textMuted }}>{total} fonts available</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: theme.bgInput, border: 'none', borderRadius: 6,
              color: theme.textMuted, width: 28, height: 28, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >&#x2715;</button>
        </div>

        {/* Search & filters */}
        <div style={{
          padding: '12px 20px', borderBottom: `1px solid ${theme.borderLight}`,
          display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search Adobe Fonts..."
            style={{
              flex: 1, padding: '8px 12px',
              background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
              borderRadius: 8, color: theme.text, fontSize: 12, outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <select
            value={category}
            onChange={e => { setCategory(e.target.value); setPage(1); }}
            style={{
              padding: '8px 10px', background: theme.bgInput,
              border: `1px solid ${theme.borderMedium}`, borderRadius: 8,
              color: theme.textSecondary, fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>
        </div>

        {/* Font list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {error && (
            <div style={{ color: theme.red, fontSize: 12, padding: 12, background: theme.redBg, borderRadius: 8, marginBottom: 12 }}>
              {error}
            </div>
          )}
          {loading ? (
            <div style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : fonts.length === 0 ? (
            <div style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', padding: 40 }}>No fonts found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {fonts.map(f => {
                const isImported = imported.has(f.family);
                const isImporting = importing === f.family;
                return (
                  <div key={f.slug} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '12px 16px', background: theme.bgTertiary,
                    border: `1px solid ${theme.borderLight}`, borderRadius: 10,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginBottom: 2 }}>
                        {f.family}
                      </div>
                      <div style={{ fontSize: 11, color: theme.textSecondary }}>
                        {f.designer}
                      </div>
                      <div style={{ fontSize: 10, color: theme.textGhost, marginTop: 2 }}>
                        {f.category} · {f.styleCount} {f.styleCount === 1 ? 'style' : 'styles'}
                      </div>
                    </div>
                    <button
                      onClick={() => !isImported && !isImporting && handleImport(f)}
                      disabled={isImported || isImporting}
                      style={{
                        padding: '7px 14px', fontSize: 11, fontWeight: 600,
                        borderRadius: 7, cursor: isImported ? 'default' : 'pointer',
                        fontFamily: 'inherit', border: 'none',
                        background: isImported ? theme.greenBg : isImporting ? theme.bgInput : 'linear-gradient(135deg, #E8524A, #FF3366)',
                        color: isImported ? theme.green : isImporting ? theme.textMuted : '#fff',
                      }}
                    >{isImported ? '\u2713 Added' : isImporting ? '...' : 'Import'}</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > 30 && (
          <div style={{
            padding: '12px 20px', borderTop: `1px solid ${theme.borderLight}`,
            display: 'flex', justifyContent: 'center', gap: 8,
          }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              style={{
                padding: '6px 12px', fontSize: 11, borderRadius: 6,
                background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                color: theme.textMuted, cursor: page <= 1 ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: page <= 1 ? 0.4 : 1,
              }}
            >Prev</button>
            <span style={{ fontSize: 11, color: theme.textMuted, display: 'flex', alignItems: 'center' }}>
              Page {page} of {Math.ceil(total / 30)}
            </span>
            <button
              disabled={page >= Math.ceil(total / 30)}
              onClick={() => setPage(p => p + 1)}
              style={{
                padding: '6px 12px', fontSize: 11, borderRadius: 6,
                background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                color: theme.textMuted, cursor: page >= Math.ceil(total / 30) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: page >= Math.ceil(total / 30) ? 0.4 : 1,
              }}
            >Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
